import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@/context/AuthContext";
import { BillingAccount } from "@/types/billing";
import { Workspace } from "@/types/workspace";
import {
  getBillingAccount,
  getWorkspaceCompanies,
  getWorkspaces,
} from "@/services/workspaceService";
import {
  pickLandingWorkspace,
  pickNextActiveWorkspace,
  workspaceHasAccess,
  workspaceIsBillingEnded,
} from "@/lib/landing";
import { isPaidPlan } from "@/lib/plans";
import { setActiveWorkspaceId } from "@/services/api";
import {
  clearWorkspaceAnalyticsContext,
  setWorkspaceAnalyticsContext,
  trackWorkspaceSwitched,
} from "@/lib/posthog";

const ACTIVE_WORKSPACE_STORAGE_KEY = "activeWorkspaceId";

interface WorkspaceContextType {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  endedWorkspace: Workspace | null;
  workspaceFallbackPending: boolean;
  billingAccount: BillingAccount | null;
  /**
   * Whether the ACTIVE WORKSPACE has paid access.
   *
   * The backend already gates workbench/sources on membership
   * (`WorkspaceHasAccess`), so a member of a paid workspace is served the data.
   * The UI asked `user.plan` instead — a viewer's own plan, which stays "free"
   * for anyone who never bought a subscription personally — so invited members
   * saw a paywall over content they could actually open.
   */
  hasPaidAccess: boolean;
  loading: boolean;
  error: string | null;
  setActiveWorkspace: (workspace: Workspace) => void;
  clearWorkspaceFallback: () => void;
  refreshWorkspaces: () => Promise<void>;
  retryWorkspaceLoad: () => Promise<void>;
  refreshBillingAccount: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const useWorkspace = (): WorkspaceContextType => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return ctx;
};

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading: authLoading, token, user } = useAuth();

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeWorkspace, setActiveWorkspaceState] = useState<Workspace | null>(null);
  const [endedWorkspace, setEndedWorkspace] = useState<Workspace | null>(null);
  const [workspaceFallbackPending, setWorkspaceFallbackPending] = useState(false);
  const [billingAccount, setBillingAccount] = useState<BillingAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const previousActiveWorkspaceRef = useRef<Workspace | null>(null);
  const activeWorkspaceRef = useRef<Workspace | null>(null);
  const tokenRef = useRef<string | null>(token);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const hydrateWorkspaceCompanies = useCallback(
    async (
      availableWorkspaces: Workspace[],
    ): Promise<{ workspaces: Workspace[]; complete: boolean }> => {
      let complete = true;
      const withCompanies = await Promise.all(
        availableWorkspaces.map(async (workspace) => {
          if (!workspaceHasAccess(workspace)) {
            return { ...workspace, companies: [] };
          }

          try {
            const companies = await getWorkspaceCompanies(workspace.id, tokenRef.current);
            return {
              ...workspace,
              companies,
            };
          } catch {
            // A failed fetch is NOT an empty workspace. Reporting it as empty
            // would let the landing resolver conclude there's nothing here and
            // move the user to a different workspace — then persist that choice,
            // making one flaky request a sticky relocation.
            complete = false;
            return {
              ...workspace,
              companies: workspace.companies ?? [],
            };
          }
        }),
      );

      return { workspaces: withCompanies, complete };
    },
    [],
  );

  const syncActiveWorkspace = useCallback((workspace: Workspace | null): void => {
    setActiveWorkspaceState(workspace);
    // Mirrored into a ref so `refreshWorkspaces` can read the current selection
    // without depending on it — taking it as a dependency would change that
    // callback's identity on every switch and re-fire the boot effect.
    activeWorkspaceRef.current = workspace;

    if (!workspace) {
      localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY);
      setActiveWorkspaceId(null);
      clearWorkspaceAnalyticsContext();
      return;
    }

    localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, String(workspace.id));
    setActiveWorkspaceId(workspace.id);
    setWorkspaceAnalyticsContext({
      workspace_id: workspace.id,
      billing_account_id: workspace.billing_account_id,
    });
  }, []);

  const fetchBillingForWorkspace = useCallback(
    async (workspace: Workspace | null): Promise<void> => {
      if (!workspace?.billing_account_id) {
        setBillingAccount(null);
        return;
      }

      try {
        const nextBillingAccount = await getBillingAccount(
          workspace.billing_account_id,
          tokenRef.current,
        );
        setBillingAccount(nextBillingAccount);
      } catch {
        // Billing account endpoint parity is still in progress on backend side.
        setBillingAccount(null);
      }
    },
    [],
  );

  const refreshWorkspaces = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      setWorkspaces([]);
      setEndedWorkspace(null);
      setWorkspaceFallbackPending(false);
      syncActiveWorkspace(null);
      setBillingAccount(null);
      return;
    }

    const nextWorkspaces = await getWorkspaces(tokenRef.current);
    const { workspaces: hydratedWorkspaces, complete } =
      await hydrateWorkspaceCompanies(nextWorkspaces);
    const activeWorkspaces = hydratedWorkspaces.filter(workspaceHasAccess);
    setWorkspaces(activeWorkspaces);

    // Mid-session refreshes (add/rename/delete a company, create a workspace)
    // must not re-litigate the choice — the user may deliberately be sitting in
    // an empty workspace. Only a boot, where nothing is active yet, runs the
    // resolver. Companies are hydrated above, so the resolver can prefer a
    // workspace that has something in it, which is how an invited member lands
    // in the workspace they were invited to rather than their empty personal one.
    const currentlyActiveId = activeWorkspaceRef.current?.id;
    const currentWorkspace =
      currentlyActiveId != null
        ? hydratedWorkspaces.find((workspace) => workspace.id === currentlyActiveId)
        : undefined;
    const stillAvailable =
      currentWorkspace && workspaceHasAccess(currentWorkspace)
        ? currentWorkspace
        : undefined;

    const persistedWorkspaceId = localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY);
    const persisted = persistedWorkspaceId
      ? hydratedWorkspaces.find((w) => String(w.id) === persistedWorkspaceId)
      : undefined;
    const selectedWorkspaceEnded =
      (currentWorkspace != null && workspaceIsBillingEnded(currentWorkspace)) ||
      (!currentWorkspace && persisted != null && workspaceIsBillingEnded(persisted));

    // If any workspace's companies failed to load, don't let the "prefer a
    // populated workspace" rule run on incomplete data — keep the persisted
    // choice and try again on the next refresh.
    const resolvedWorkspace =
      stillAvailable ??
      (selectedWorkspaceEnded
        ? pickNextActiveWorkspace(hydratedWorkspaces)
        : complete
          ? pickLandingWorkspace(hydratedWorkspaces, persistedWorkspaceId)
          : persisted && workspaceHasAccess(persisted)
            ? persisted
            : pickLandingWorkspace(hydratedWorkspaces, persistedWorkspaceId));

    const selectedEndedWorkspace =
      currentWorkspace && workspaceIsBillingEnded(currentWorkspace)
        ? currentWorkspace
        : persisted && workspaceIsBillingEnded(persisted)
          ? persisted
          : hydratedWorkspaces.find(
              workspaceIsBillingEnded,
            ) ?? null;

    setEndedWorkspace(resolvedWorkspace ? null : selectedEndedWorkspace);
    setWorkspaceFallbackPending(Boolean(selectedWorkspaceEnded && resolvedWorkspace));
    syncActiveWorkspace(resolvedWorkspace);
    await fetchBillingForWorkspace(resolvedWorkspace);
  }, [
    fetchBillingForWorkspace,
    hydrateWorkspaceCompanies,
    isAuthenticated,
    syncActiveWorkspace,
  ]);

  const refreshBillingAccount = useCallback(async (): Promise<void> => {
    await fetchBillingForWorkspace(activeWorkspace);
  }, [activeWorkspace, fetchBillingForWorkspace]);

  const clearWorkspaceFallback = useCallback((): void => {
    setWorkspaceFallbackPending(false);
  }, []);

  const retryWorkspaceLoad = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await refreshWorkspaces();
    } catch (loadError) {
      console.error("Failed to load workspaces:", loadError);
      setError("Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [refreshWorkspaces]);

  const setActiveWorkspace = useCallback(
    (workspace: Workspace): void => {
      const resolvedWorkspace =
        workspaces.find((availableWorkspace) => availableWorkspace.id === workspace.id) ??
        workspace;

      syncActiveWorkspace(resolvedWorkspace);
      fetchBillingForWorkspace(resolvedWorkspace);
    },
    [fetchBillingForWorkspace, syncActiveWorkspace, workspaces],
  );

  useEffect(() => {
    const previousWorkspace = previousActiveWorkspaceRef.current;

    if (
      previousWorkspace &&
      activeWorkspace &&
      previousWorkspace.id !== activeWorkspace.id
    ) {
      trackWorkspaceSwitched({
        workspace_id: activeWorkspace.id,
        previous_workspace_id: previousWorkspace.id,
        workspace_name: activeWorkspace.name,
        user_role: activeWorkspace.role,
      });
    }

    previousActiveWorkspaceRef.current = activeWorkspace;
  }, [activeWorkspace]);

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setWorkspaces([]);
      setEndedWorkspace(null);
      setWorkspaceFallbackPending(false);
      syncActiveWorkspace(null);
      setBillingAccount(null);
      setError(null);
      setLoading(false);
      return;
    }

    void retryWorkspaceLoad();
  }, [authLoading, isAuthenticated, retryWorkspaceLoad, syncActiveWorkspace]);

  // Falls back to the viewer's own plan only when there is no billing account to
  // read at all. Nothing renders before it loads — PrivateRoute blocks until the
  // workspace (and its billing) has resolved — so this is not a paywall flash.
  const hasPaidAccess = billingAccount
    ? isPaidPlan(billingAccount.plan)
    : isPaidPlan(user?.plan);

  const value = useMemo<WorkspaceContextType>(
    () => ({
      workspaces,
      activeWorkspace,
      endedWorkspace,
      workspaceFallbackPending,
      billingAccount,
      hasPaidAccess,
      loading,
      error,
      setActiveWorkspace,
      clearWorkspaceFallback,
      refreshWorkspaces,
      retryWorkspaceLoad,
      refreshBillingAccount,
    }),
    [
      workspaces,
      activeWorkspace,
      endedWorkspace,
      workspaceFallbackPending,
      billingAccount,
      hasPaidAccess,
      loading,
      error,
      setActiveWorkspace,
      clearWorkspaceFallback,
      refreshWorkspaces,
      retryWorkspaceLoad,
      refreshBillingAccount,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>
  );
};
